import {
  CURRENCY_LABEL,
  TARIFF_ACTIONS,
  calcDiscount,
  getBaseCost,
  getTariffAction,
  useBillingInfoGetter,
  useButtonLabelGetter,
} from '@/helpers/payments';
import { ArrowDownwardIcon, ArrowForwardIcon } from '@/icons';
import { Box, Button, Divider, Typography } from '@mui/material';
import { SubscriptionPlanDto, SubscriptionType } from '@tailored-pdf/api';
import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Discount, Price, PricePeriod, PriceTitle, SubscriptionCard } from './styles';

type Props = {
  plan?: SubscriptionPlanDto;
  plans?: SubscriptionPlanDto[];
  onAction?: (plan: SubscriptionPlanDto) => void;
  nextSubscriptionType?: SubscriptionType;
  nextSubscriptionStartDate?: unknown;
  showBillingInfo?: boolean;
};

function getButtonIcon(tariff?: SubscriptionType, plans?: SubscriptionPlanDto[]) {
  if (!plans || !tariff) {
    return null;
  }

  const action = getTariffAction(tariff, plans);

  if (action === TARIFF_ACTIONS.DOWNGRADE) {
    return <ArrowDownwardIcon />;
  } else if (action === TARIFF_ACTIONS.UPGRADE) {
    return <ArrowForwardIcon />;
  }

  return null;
}

function isCurrentTariff(tariff?: SubscriptionType, plans?: SubscriptionPlanDto[]) {
  return tariff && plans ? !!plans.find((v) => v.subscriptionType === tariff)?.active : false;
}

export function TariffCard({
  plan,
  plans,
  onAction,
  nextSubscriptionType,
  nextSubscriptionStartDate,
  showBillingInfo = true,
}: Props) {
  const { t } = useTranslation('web', { keyPrefix: 'tariffs' });
  const getButtonLabel = useButtonLabelGetter();
  const getBillingInfo = useBillingInfoGetter();

  const handleAction = useCallback(
    () => plan && plan.subscriptionType !== nextSubscriptionType && onAction?.(plan),
    [nextSubscriptionType, onAction, plan],
  );

  const basePrice = plan ? getBaseCost(plan) : 0;
  const discount = plan ? calcDiscount(plan.subscriptionType) : 0;
  const periodName = plan && plan.subscriptionType == SubscriptionType.TRIAL ? <br /> : `/ ${t('basePaymentPeriod')}`;

  return (
    <SubscriptionCard>
      {discount ? <Discount>{`-${discount}%`}</Discount> : null}
      <Box display={'flex'} flexDirection={'column'} gap={6} flex={'1 1 auto'}>
        <Box display={'flex'} flexDirection={'column'} gap={1}>
          <PriceTitle>{plan ? t(`names.${plan.subscriptionType}`) : ''}</PriceTitle>
          <Price>{plan ? `${CURRENCY_LABEL}${basePrice}` : `0${CURRENCY_LABEL}`}</Price>
          <PricePeriod>{periodName}</PricePeriod>
        </Box>
        <Typography paddingRight={2} paddingLeft={2} textAlign={'center'}>
          {basePrice ? t('downloads.paid') : t('downloads.free')}
        </Typography>
        <Divider />
        <Button
          disableElevation
          disabled={!plan}
          className={isCurrentTariff(plan?.subscriptionType, plans) ? 'buttonSecondary' : undefined}
          variant={isCurrentTariff(plan?.subscriptionType, plans) ? 'outlined' : 'contained'}
          endIcon={getButtonIcon(plan?.subscriptionType, plans)}
          onClick={handleAction}
        >
          {getButtonLabel(plan?.subscriptionType, plans)}
        </Button>

        {plan && plan.subscriptionType === nextSubscriptionType ? (
          <Typography paddingRight={2} paddingLeft={2} textAlign={'center'} fontSize={'14px'} fontWeight={600}>
            {t('willActivate')} {new Date((nextSubscriptionStartDate as number) * 1000).toLocaleDateString('en-GB')}
          </Typography>
        ) : null}

        {showBillingInfo ? (
          <Typography paddingRight={2} paddingLeft={2} textAlign={'center'} fontSize={'14px'} color={'text.secondary'}>
            {plan && plans ? getBillingInfo(plan.subscriptionType, plans) : ''}
          </Typography>
        ) : null}
      </Box>
    </SubscriptionCard>
  );
}
